import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WorksheetService } from 'src/app/features/worksheet/services/worksheet.service';
import { AsReceivedHistoryEvent, AsReceivedHistoryRow, HistoryField } from 'src/app/features/worksheet/models/native-soil-worksheet.modal';
import { FormService } from 'src/app/shared/services/form.service';
import { plural } from 'src/app/shared/components/history-timeline/history-timeline.component';
import { HistoryChange } from 'src/app/shared/models/history-timeline.modal';

/**
 * Timeline of saves and assignments on the As-Received worksheet. Each entry shows the whole worksheet as it
 * stood after the action; the rows it changed are highlighted, with the previous value of each changed field.
 */
@Component({
    selector: 'app-as-received-history',
    templateUrl: './as-received-history.component.html',
    styleUrls: ['./as-received-history.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AsReceivedHistoryComponent implements OnInit {
  projectId: string | null = null;
  companyName = '';
  projectName = '';
  referenceId = '';
  // Newest first: the most recent action is the leftmost point on the timeline
  events: AsReceivedHistoryEvent[] = [];
  selectedIndex = -1;
  isLoading = true;
  // Same order as the As-Received worksheet table
  displayedColumns = ['inHouseId', 'sampleId', 'depth', 'collectionDate', 'wetWeight', 'dryWeight', 'assignedTo', 'status'];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private worksheetService: WorksheetService,
    private formService: FormService,
  ) {}

  ngOnInit(): void {
    this.projectId = this.route.snapshot.queryParamMap.get('projectId');
    if (!this.projectId) {
      this.isLoading = false;
      return;
    }

    this.formService.fetchFormData(this.projectId, 'nativesoil').subscribe({
      next: (result) => {
        this.companyName = result?.company?.name ?? '';
        this.projectName = result?.project?.projectName ?? '';
        this.referenceId = result?.project?.referenceId ?? '';
      },
    });

    this.worksheetService.getAsReceivedHistory(this.projectId).subscribe({
      next: (events) => {
        // The API returns newest first, which is also the timeline's left-to-right order
        this.events = (Array.isArray(events) ? events : []).map((event) => ({ ...event, changes: this.buildChanges(event) }));
        this.isLoading = false;
        // Open on the latest action (the leftmost point)
        this.selectedIndex = this.events.length > 0 ? 0 : -1;
      },
      error: () => {
        this.events = [];
        this.isLoading = false;
      },
    });
  }

  get selectedEvent(): AsReceivedHistoryEvent | null {
    return this.events[this.selectedIndex] ?? null;
  }

  backToWorksheet(): void {
    this.router.navigate(['as-received-worksheet'], { queryParams: { projectId: this.projectId } });
  }

  reDirectUrl(): void {
    this.router.navigate(['dashboard']);
  }

  displayDepth(row: AsReceivedHistoryRow): string {
    if (row.depth === null || row.depth === undefined) {
      return '';
    }
    return row.endDepth !== null && row.endDepth !== undefined ? `${row.depth}-${row.endDepth}` : `${row.depth}`;
  }

  displayValue(value: string | number | boolean | null | undefined, isStatus = false): string {
    if (isStatus) {
      return value ? 'Completed' : 'Not completed';
    }
    return value === null || value === undefined || value === '' ? '—' : `${value}`;
  }

  // Same group line as the worksheet: a thick border under the last row of each sample
  isLastRowOfSample(rows: AsReceivedHistoryRow[], index: number): boolean {
    const nextRow = rows[index + 1];
    return !!nextRow && nextRow.sampleId !== rows[index].sampleId;
  }

  changedRowCount(event: AsReceivedHistoryEvent): number {
    return event.rows.filter((row) => row.changedInAction).length;
  }

  changedFieldCount(event: AsReceivedHistoryEvent): number {
    const fields = (row: AsReceivedHistoryRow): HistoryField<unknown>[] =>
      [row.inHouseId, row.wetWeight, row.dryWeight, row.assignedTo, row.status];
    return event.rows.reduce((total, row) => total + fields(row).filter((field) => field.changed).length, 0);
  }

  // Timeline icons: edits and assignments with their counts (As-Received rows aren't added or removed here)
  private buildChanges(event: AsReceivedHistoryEvent): HistoryChange[] {
    const valueFields = (row: AsReceivedHistoryRow): HistoryField<unknown>[] =>
      [row.inHouseId, row.wetWeight, row.dryWeight, row.status];
    const editedValues = event.rows.reduce((total, row) => total + valueFields(row).filter((field) => field.changed).length, 0);
    const firstSaves = event.rows.filter((row) => row.isFirstRecord).length;
    const assigned = event.rows.filter((row) => row.assignedTo.changed).length;

    const changes: HistoryChange[] = [];
    const edits = [
      editedValues > 0 ? `${plural(editedValues, 'value')} edited` : '',
      firstSaves > 0 ? `${plural(firstSaves, 'row')} saved for the first time` : '',
    ].filter(Boolean);
    if (edits.length > 0) {
      changes.push({ kind: 'edited', tooltip: edits.join(', ') });
    }
    if (assigned > 0) {
      changes.push({ kind: 'assigned', tooltip: `${plural(assigned, 'row')} assigned` });
    }
    return changes;
  }
}
